import type { WebappResponse } from '@eventbrite/event-listing-service-client';

import { isEventEnded } from '../../src/components/Body/utils/bodyUtils';
import { getRefundPolicy, isYTShortVerticalVideo } from '../../src/utils/utils';

/**
 * Statsig user custom properties for the listing page.
 * Keys and semantics are aligned with ebui's listings package so experiments
 * and analytics stay consistent across listings-webapp and eb-ui listings.
 */
export type StatsigListingsUserCustom = Record<string, string | number | boolean | undefined>;

function normalizeTaxonomy(value: string | undefined): string {
  return value?.toLocaleLowerCase().replace(/\s/g, '_') || '';
}

/**
 * Builds the Statsig user custom object from listing context.
 */
export function buildListingsUserCustom(
  context: WebappResponse,
  userIsAuthenticated = false,
): StatsigListingsUserCustom {
  const { basicInfo, gallery, taxonomies, goodToKnow, structuredContent, salesStatus } = context;

  // Check for hero video
  const featuredVideoUrl = gallery?.featuredVideo?.url;
  const nativeVideoUrl = gallery?.nativeVideo?.url;
  const hasHeroVideo = !!(featuredVideoUrl || nativeVideoUrl);
  const hasHeroVideoVertical = isYTShortVerticalVideo(featuredVideoUrl);

  // Good to Know section
  const goodToKnowAge = !!goodToKnow?.highlights?.ageRestriction;
  const goodToKnowCheckin = !!goodToKnow?.highlights?.doorTime;
  const goodToKnowParking = getParkingInfo(goodToKnow?.highlights);
  const goodToKnowFAQNumber = structuredContent?.widgets?.faqs?.length || 0;
  const eventRefund = getRefundPolicy(goodToKnow?.refundPolicy);

  const custom: StatsigListingsUserCustom = {
    // Event Identifiers
    eventId: basicInfo.id,
    organizerId: basicInfo.organizationId,
    category: normalizeTaxonomy(taxonomies?.category),
    subcategory: normalizeTaxonomy(taxonomies?.subcategory),

    // Event State
    eventStatus: basicInfo.status,
    isLive: basicInfo.status === 'live',
    listed: basicInfo.isListed,
    isFree: basicInfo.isFree,
    isPaid: !basicInfo.isFree,
    salesStatus: salesStatus?.salesStatus,
    isExternalTicketAvailable: basicInfo.externalTicketing?.externalTicketsAreAvailable ?? false,

    // Event Type
    onlineEvent: basicInfo.isOnline ?? false,
    isSeriesParent: basicInfo.isParentEvent ?? false,
    isSeries: basicInfo.isSeries ?? false,
    isRepeatingEvent: basicInfo.isSeries ?? false,
    protected: basicInfo.isProtected ?? false,

    // Venue/Time
    venueCountry: basicInfo.venue?.address?.country,
    venueRegion: basicInfo.venue?.address?.region || undefined,
    timeZone: basicInfo.startDate?.timezone,

    // Media
    hasImages: (gallery?.images?.length ?? 0) > 0,
    hasHeroVideo,
    hasHeroVideoVertical,

    // Good to Know
    goodToKnowAge,
    goodToKnowCheckin,
    goodToKnowParking,
    goodToKnowFAQNumber,
    eventRefund,

    // User state
    userIsAuthenticated,
  };

  return Object.fromEntries(
    Object.entries(custom).filter(([, v]) => v !== undefined && v !== ''),
  ) as StatsigListingsUserCustom;
}

/**
 * Get parking info string matching ebui's getParkingInfo logic.
 */
function getParkingInfo(highlights?: {
  parking?: string;
  freeParking?: boolean;
  paidParking?: boolean;
  notAvailableParking?: boolean;
}): string | undefined {
  if (!highlights) return undefined;
  if (highlights.parking) return highlights.parking;
  if (highlights.freeParking) return 'free';
  if (highlights.paidParking) return 'paid';
  if (highlights.notAvailableParking) return 'not_available';
  return undefined;
}
